/**
 * Tasks Service
 * API service for task management (public.tasks).
 *
 * public.tasks columns (QA and PROD, verified 2026-10-16): id, title, description,
 * status (task_status enum: to_do|in_progress|completed|blocked|cancelled),
 * priority (INTEGER 1-5, default 3), due_date (DATE), assigned_to, organization_id,
 * location_id, event_id, contact_id, created_by, created_at, updated_at, completed_at.
 * Writes go through toTaskWrite() so only real columns, with the right types, are sent.
 */

import { supabase } from "./supabase";
import { toTaskPriority, TaskStatus } from "../types/task.types";
import type { Task, TaskFilters } from "../types/task.types";
import type { UUID } from "../types/common.types";
import type { PaginatedResponse, PaginationParams } from "../types/api.types";
import { sanitizePostgrestSearchTerm } from "../utils/postgrestSearch";

/** The columns a create/update may write. Anything else is a PGRST204 waiting to happen. */
export type TaskWrite = Pick<
  Task,
  | "title"
  | "description"
  | "status"
  | "priority"
  | "due_date"
  | "assigned_to"
  | "organization_id"
  | "contact_id"
  | "event_id"
> & { created_by?: UUID | null };

/** Raw form values (strings from inputs/selects). */
export interface TaskFormValues {
  title: string;
  description: string;
  status: string;
  priority: string | number;
  due_date: string;
  assigned_to: string;
  organization_id: string;
  contact_id: string;
  event_id: string;
}

const TASK_STATUS_VALUES = Object.values(TaskStatus) as string[];

/**
 * Form values -> a write payload with DB types: priority as an integer 1-5, "" as NULL
 * (so clearing a field on edit really clears it), status checked against the enum.
 */
export const toTaskWrite = (form: TaskFormValues): TaskWrite => {
  const nullable = (v: string) => (v && v.trim() ? v.trim() : null);
  if (!TASK_STATUS_VALUES.includes(form.status)) {
    throw new Error(`Unknown task status: ${form.status}`);
  }
  return {
    title: form.title.trim(),
    description: nullable(form.description),
    status: form.status as TaskStatus,
    priority: toTaskPriority(form.priority),
    due_date: nullable(form.due_date),
    assigned_to: nullable(form.assigned_to),
    organization_id: nullable(form.organization_id),
    contact_id: nullable(form.contact_id),
    event_id: nullable(form.event_id),
  };
};

export const getTasks = async (
  params: PaginationParams,
  filters?: TaskFilters,
): Promise<PaginatedResponse<Task>> => {
  let query = supabase.from("tasks").select("*", { count: "exact" });

  if (filters?.status && filters.status.length > 0) {
    query = query.in("status", filters.status);
  }
  if (filters?.priority && filters.priority.length > 0) {
    query = query.in("priority", filters.priority);
  }
  if (filters?.assigned_to) {
    query = query.eq("assigned_to", filters.assigned_to);
  }
  const term = filters?.search
    ? sanitizePostgrestSearchTerm(filters.search)
    : "";
  if (term) {
    query = query.or(`title.ilike.*${term}*,description.ilike.*${term}*`);
  }

  const { data, error, count } = await query
    .order(params.sortBy || "created_at", {
      ascending: params.sortOrder === "asc",
      nullsFirst: false,
    })
    .order("created_at", { ascending: false })
    .range((params.page - 1) * params.limit, params.page * params.limit - 1);

  if (error) {
    throw error;
  }
  const total = count || 0;
  return {
    data: (data || []) as Task[],
    total,
    page: params.page,
    limit: params.limit,
    totalPages: Math.ceil(total / params.limit),
    hasMore: params.page * params.limit < total,
  };
};

export const getTask = async (id: UUID): Promise<Task> => {
  const { data, error } = await supabase
    .from("tasks")
    .select("*")
    .eq("id", id)
    .single();
  if (error) {
    throw error;
  }
  return data as Task;
};

export const createTask = async (task: TaskWrite): Promise<Task> => {
  const { data, error } = await supabase
    .from("tasks")
    .insert([task])
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as Task;
};

/**
 * Update a task. RLS lets you update a task only if you created it, it is assigned to you,
 * you have access to its organization, or you are an admin; otherwise zero rows match and
 * PostgREST answers PGRST116 to `.single()`, reported here in words.
 */
export const updateTask = async (
  id: UUID,
  updates: Partial<TaskWrite>,
): Promise<Task> => {
  const { data, error } = await supabase
    .from("tasks")
    .update(updates)
    .eq("id", id)
    .select()
    .maybeSingle();
  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error(
      "This task was not updated: it no longer exists or you are not allowed to change it.",
    );
  }
  return data as Task;
};

/** Delete a task. A delete RLS refuses matches zero rows silently, so we check the count. */
export const deleteTask = async (id: UUID): Promise<void> => {
  const { data, error } = await supabase
    .from("tasks")
    .delete()
    .eq("id", id)
    .select("id");
  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error(
      "This task was not deleted: only its creator or an admin can delete it.",
    );
  }
};

/**
 * Get count of outstanding tasks assigned to a user
 * Outstanding = status is 'to_do' or 'in_progress'
 */
export const getOutstandingTasksCount = async (
  userId: UUID,
): Promise<number> => {
  const { count, error } = await supabase
    .from("tasks")
    .select("*", { count: "exact", head: true })
    .eq("assigned_to", userId)
    .in("status", [TaskStatus.TODO, TaskStatus.IN_PROGRESS]);

  if (error) {
    console.error("Error fetching outstanding tasks count:", error);
    return 0;
  }

  return count || 0;
};

export interface TaskAssignee {
  id: UUID;
  name: string;
}

/**
 * People the current user may assign a task to: active superadmin/admin/manager/staff, as (id, name).
 * SECURITY DEFINER RPC (migration 2026-10-16-897). It exists because profiles RLS shows managers and
 * staff only their own row, so a direct SELECT on profiles listed only "me" for them.
 * Refuses (42501) callers who are not active Tasks users; that is thrown, never an empty list.
 */
export const listTaskAssignees = async (): Promise<TaskAssignee[]> => {
  const { data, error } = await supabase.rpc("list_task_assignees");
  if (error) {
    throw error;
  }
  return (data ?? []) as TaskAssignee[];
};
